import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Authorization,
  AuthorizationDocument,
  AuthorizationDiscipline,
  PAStatus,
  PAPayerType,
  PAUnitType,
  PARequestType,
  ServiceCategory,
  MedicaidProgram,
  PAServiceType,
  PALinkedFormType,
  TherapyAcuity,
  AttendantProgram,
  PACreateRequest,
  PADocument,
  PAFlowSheetError,
  PAPacketResponse,
  PASubmitWarning
} from '../../../dtos/patientmr/authorizations.dto';
import {
  AuthorizationsService,
  AuthorizationRequest,
  PA_COPYABLE_STATUSES,
  PA_DOCUMENT_DELETE_STATUSES
} from '../../../services/patientmr/authorizations.service';
import { getAgencyProfileMissing } from '../../../services/admin/agency.service';
import ConfirmDialog from '../../common/ConfirmDialog';
import { useToast } from '../../common/Toast';
import { isApiError } from '../../../dtos/common/api.dto';
import { describeApiError } from '../../../utils/apiErrors';
import { SvcBadge } from './components/SvcBadge';
import { PayerChip } from './components/PayerChip';
import { GuideBanner } from './sections/GuideBanner';
import { PayerSection } from './sections/PayerSection';
import { RequestSection } from './sections/RequestSection';
import { FormAssociationSection } from './sections/FormAssociationSection';
import { ApprovalSection } from './sections/ApprovalSection';
import { UsageTrackerSection } from './sections/UsageTrackerSection';
import { LinkedFlowSheetSection } from './sections/LinkedFlowSheetSection';
import { DeadlinesBanner, FlowSheetErrorsBanner, PacketBanner, SubmitWarningsList } from './sections/SubmitFeedback';
import { FormPageLayout, DocumentUpload } from '../../common';
import type { ActionButtonConfig, DocumentUploadDoc } from '../../common';
import { getPaCategoryEntry } from '../../../constants/disciplines';

/** Domain errors (RECORD_LOCKED, INVALID_TRANSITION, ...) get a friendly message; others keep the server text. */
const paErrorMessage = (err: unknown, fallback: string): string => {
  if (isApiError(err) && err.domainCode) return describeApiError(err, fallback);
  return err instanceof Error && err.message ? err.message : fallback;
};

// ── Helper: map documents to DocumentUploadDoc ─────────────────────────────

type StoredDoc = AuthorizationDocument & { fileSize?: number; deletedAt?: string | null };

function mapAuthDocs(docs: Authorization['documents']): DocumentUploadDoc[] {
  return (docs || [])
    .filter((d): d is StoredDoc => typeof d !== 'string' && !!d.documentId && !(d as StoredDoc).deletedAt)
    .map(d => ({
      id: d.documentId as string,
      name: d.documentName || d.name || 'Document',
      size: d.fileSize,
      uploadedAt: d.uploadedAt,
    }));
}

function toUploadDoc(d: PADocument): DocumentUploadDoc {
  return { id: d.id, name: d.fileName, size: d.fileSize || undefined, uploadedAt: d.uploadedAt || undefined };
}

const authIdOf = (a: Authorization): string => a.authorizationId || String(a.id);

/** Medicaid PDN PAs need a linked, finalized, complete F00120 to submit (§3.6a; aide PAs no longer do, D-7). */
const needsFlowSheet = (payerType: string, serviceCategory: string): boolean =>
  payerType === PAPayerType.MEDICAID && getPaCategoryEntry(serviceCategory)?.flowsheetGated === true;

// ── PA Form State ─────────────────────────────────────────────────────────

interface PAFormState {
  payerType: string;
  medicaidProgram: string;
  payerName: string;
  serviceCategory: string;
  requestType: string;
  unitType: string;
  minutesPerUnit: number | null;
  requestedStartDate: string;
  requestedEndDate: string;
  revisionReason: string;
  therapyAcuity: string;
  attendantProgram: string;
  linkedFormId: string;
  linkedFormType: string;
  linkedFlowSheetFormId: string;
  // Additional fields for create/edit
  discipline: string;
  authNumber: string;
  authorizedVisits: number | string;
  notes: string;
}

const DEFAULT_PA_FORM: PAFormState = {
  payerType: '',
  medicaidProgram: '',
  payerName: '',
  serviceCategory: '',
  requestType: PARequestType.INITIAL,
  unitType: '',
  minutesPerUnit: null,
  requestedStartDate: '',
  requestedEndDate: '',
  revisionReason: '',
  therapyAcuity: '',
  attendantProgram: '',
  linkedFormId: '',
  linkedFormType: '',
  linkedFlowSheetFormId: '',
  discipline: '',
  authNumber: '',
  authorizedVisits: 0,
  notes: ''
};

// ── Props ─────────────────────────────────────────────────────────────────

interface AuthorizationDetailProps {
  authorization?: Authorization | null;
  patientId: string;
  patientName?: string;
  medicaidId?: string;
  mode: 'create' | 'edit' | 'view';
  onBack: () => void;
  onSaved: (createdAuth?: Authorization) => void;
  onError: (msg: string) => void;
  isReadOnlyView?: boolean;
  canUpdateAuthorization?: boolean;
  canDeleteAuthorization?: boolean;
}

// ── StatusBadge (local helper) ────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = AuthorizationsService.getPAStatusBadgeStyles(status);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
      {status}
    </span>
  );
};

/** Pending submit confirmation: the PA to submit and the record to hand back on cancel. */
interface PendingSubmit {
  authId: string;
  warnings: PASubmitWarning[];
  fallback: Authorization | null;
}

// ── Component ─────────────────────────────────────────────────────────────

export const AuthorizationDetail: React.FC<AuthorizationDetailProps> = ({
  authorization, patientId, patientName, medicaidId, mode, onBack, onSaved, onError,
  isReadOnlyView = false,
  canUpdateAuthorization = true,
  canDeleteAuthorization = false
}) => {
  const isNew = mode === 'create';
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const status = (authorization?.status as PAStatus) || PAStatus.DRAFT;
  const { showToast } = useToast();

  // Form state for create/edit
  const [form, setForm] = useState<PAFormState>(() => {
    if (authorization && (isEdit || isView)) {
      return {
        payerType: authorization.payerType || '',
        medicaidProgram: authorization.medicaidProgram || '',
        payerName: authorization.payerName || '',
        serviceCategory: authorization.serviceCategory || '',
        requestType: authorization.requestType || PARequestType.INITIAL,
        unitType: authorization.unitType || '',
        minutesPerUnit: authorization.minutesPerUnit || null,
        requestedStartDate: authorization.requestedStartDate || authorization.startDate || '',
        requestedEndDate: authorization.requestedEndDate || authorization.expirationDate || '',
        revisionReason: authorization.revisionReason || '',
        therapyAcuity: authorization.therapyAcuity || '',
        attendantProgram: authorization.attendantProgram || '',
        linkedFormId: authorization.linkedFormId || '',
        linkedFormType: authorization.linkedFormType || '',
        linkedFlowSheetFormId: authorization.linkedFlowSheetFormId || '',
        discipline: authorization.discipline || '',
        authNumber: authorization.authNumber || '',
        authorizedVisits: authorization.authorizedVisits || 0,
        notes: authorization.notes || ''
      };
    }
    return DEFAULT_PA_FORM;
  });

  const [saving, setSaving] = useState(false);
  const [savingTransition, setSavingTransition] = useState(false);
  const [confirmDenyOpen, setConfirmDenyOpen] = useState(false);
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    authNumber: '',
    approvedStartDate: '',
    approvedEndDate: '',
    approvedQuantity: '' as number | string,
    approvedPerWeek: '' as number | string,
    serviceType: '',
    uaApproved: false,
  });

  // Submit preflight / gate
  const [pendingSubmit, setPendingSubmit] = useState<PendingSubmit | null>(null);
  const [flowSheetErrors, setFlowSheetErrors] = useState<PAFlowSheetError[] | null>(null);

  // Packet
  const [packetLoading, setPacketLoading] = useState(false);
  const [packetResult, setPacketResult] = useState<PAPacketResponse | null>(null);
  const [packetError, setPacketError] = useState<{ message: string; missing: string[] } | null>(null);

  // Create mode: files are staged and uploaded right after the first save
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Create mode: the PA already created by an earlier Submit attempt (never create twice)
  const createdRef = useRef<Authorization | null>(null);

  // Edit/view document list and upload state (pre-signed S3 POST, real progress)
  const [viewDocs, setViewDocs] = useState<DocumentUploadDoc[]>(() => mapAuthDocs(authorization?.documents || []));
  const [uploadProgress, setUploadProgress] = useState<{ name: string; pct: number } | null>(null);
  const [viewDeletingId, setViewDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentUploadDoc | null>(null);

  // Computed values
  const daysLeft = authorization?.approvedEndDate ? AuthorizationsService.getDaysRemaining(authorization.approvedEndDate) : null;
  const isApprovedLike = status === PAStatus.APPROVED || status === PAStatus.EXPIRED || status === PAStatus.EXHAUSTED;
  // PA records: editable only in DRAFT/PENDED (enforced by the server). Legacy records (no serviceCategory/payerType): always editable.
  const isPARecord = !!(authorization?.serviceCategory || authorization?.payerType);
  const canEdit = canUpdateAuthorization && (!isPARecord || status === PAStatus.DRAFT || status === PAStatus.PENDED);
  const canDeleteDocs = canUpdateAuthorization && !isReadOnlyView && PA_DOCUMENT_DELETE_STATUSES.has(status);
  // Phase 20D: attendant care has no packet (the MCO assesses and authorizes it)
  const canGeneratePacket = canUpdateAuthorization && !isReadOnlyView && (status === PAStatus.DRAFT || status === PAStatus.PENDED)
    && authorization?.serviceCategory !== 'attendant';
  const uploading = uploadProgress !== null;

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleFormChange = useCallback((field: string, value: string | number | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Load documents for the current PA (list route; falls back to the record's array) ──

  const authKey = authorization ? authIdOf(authorization) : null;
  useEffect(() => {
    if (!authorization || !authKey) {
      setViewDocs([]);
      return undefined;
    }
    let cancelled = false;
    setViewDocs(mapAuthDocs(authorization.documents || []));
    AuthorizationsService.listDocuments(patientId, authKey)
      .then(docs => { if (!cancelled) setViewDocs(docs.map(toUploadDoc)); })
      .catch(() => { /* keep the documents on the record */ });
    return () => { cancelled = true; };
  }, [authorization, authKey, patientId]);

  // ── Documents ─────────────────────────────────────────────────────────

  /** Upload files to a saved PA one by one; returns the messages of any that failed. */
  const uploadFilesTo = useCallback(async (authId: string, files: File[]): Promise<string[]> => {
    const failures: string[] = [];
    for (const file of files) {
      setUploadProgress({ name: file.name, pct: 0 });
      try {
        const doc = await AuthorizationsService.uploadDocument(
          patientId,
          authId,
          file,
          pct => setUploadProgress({ name: file.name, pct })
        );
        if (doc) setViewDocs(prev => [...prev.filter(d => d.id !== doc.id), toUploadDoc(doc)]);
      } catch (err) {
        failures.push(err instanceof Error && err.message ? err.message : `"${file.name}" failed to upload.`);
      }
    }
    setUploadProgress(null);
    return failures;
  }, [patientId]);

  // Create mode: stage pre-validated files (uploaded after the first save)
  const handleFileSelect = useCallback((files: File[]) => {
    setPendingFiles(prev => [...prev, ...files]);
  }, []);

  // Edit/view: upload right away
  const handleViewUpload = useCallback(async (files: File[]) => {
    if (!authorization) return;
    const failures = await uploadFilesTo(authIdOf(authorization), files);
    if (failures.length > 0) onError(failures.join(' '));
  }, [authorization, uploadFilesTo, onError]);

  const handleViewDownload = useCallback(async (doc: DocumentUploadDoc) => {
    if (!authorization) return;
    try {
      const url = await AuthorizationsService.getDocumentUrl(patientId, authIdOf(authorization), doc.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      onError(err instanceof Error && err.message ? err.message : 'Download failed');
    }
  }, [authorization, patientId, onError]);

  const confirmDeleteDocument = useCallback(async () => {
    if (!authorization || !deleteTarget) return;
    const doc = deleteTarget;
    setViewDeletingId(doc.id);
    try {
      await AuthorizationsService.deleteDocument(patientId, authIdOf(authorization), doc.id);
      setViewDocs(prev => prev.filter(d => d.id !== doc.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteTarget(null);
      onError(paErrorMessage(err, 'Delete failed'));
    } finally {
      setViewDeletingId(null);
    }
  }, [authorization, deleteTarget, patientId, onError]);

  // ── Save helpers ──────────────────────────────────────────────────────

  const missingRequiredFields = (): string[] => {
    const missingFields: string[] = [];
    if (!form.payerType) missingFields.push('Payer Type');
    if (form.payerType === PAPayerType.MEDICAID && !form.medicaidProgram) missingFields.push('Medicaid Program');
    if (!form.payerName || form.payerName.trim().length < 2) missingFields.push('Payer Name');
    if (!form.serviceCategory) missingFields.push('Service Category');
    if (!form.requestType) missingFields.push('Request Type');
    if (!form.unitType) missingFields.push('Authorized In');
    if (form.unitType === PAUnitType.UNITS && (!form.minutesPerUnit || form.minutesPerUnit < 1 || form.minutesPerUnit > 120)) missingFields.push('Minutes per Unit (1–120)');
    if (!form.requestedStartDate) missingFields.push('Start Date');
    if (!form.requestedEndDate) missingFields.push('End Date');
    return missingFields;
  };

  const authorizedVisitsNumber = (): number =>
    typeof form.authorizedVisits === 'string' ? parseInt(form.authorizedVisits) || 0 : form.authorizedVisits;

  const buildCreatePayload = (): PACreateRequest => ({
    payerType: form.payerType as PAPayerType,
    medicaidProgram: (form.medicaidProgram as MedicaidProgram) || null,
    payerName: form.payerName,
    serviceCategory: form.serviceCategory as ServiceCategory,
    requestType: form.requestType as PARequestType,
    unitType: form.unitType as PAUnitType,
    minutesPerUnit: form.minutesPerUnit,
    requestedStartDate: form.requestedStartDate,
    requestedEndDate: form.requestedEndDate,
    revisionReason: form.revisionReason || null,
    linkedFormId: form.linkedFormId || null,
    linkedFormType: (form.linkedFormType as PALinkedFormType) || null,
    linkedFlowSheetFormId: form.linkedFlowSheetFormId || null,
    therapyAcuity: (form.therapyAcuity as TherapyAcuity) || null,
    attendantProgram: (form.attendantProgram as AttendantProgram) || null,
    discipline: form.discipline || undefined,
    startDate: form.requestedStartDate,
    expirationDate: form.requestedEndDate,
    authorizedVisits: authorizedVisitsNumber(),
  });

  const buildUpdatePayload = (existing: Authorization): AuthorizationRequest => ({
    discipline: (form.discipline as AuthorizationDiscipline) || existing.discipline,
    startDate: form.requestedStartDate,
    expirationDate: form.requestedEndDate,
    // Stripped by the service (set by the approve action, Phase 4)
    authNumber: existing.authNumber,
    authorizedVisits: authorizedVisitsNumber(),
    notes: form.notes,
    payerType: form.payerType,
    medicaidProgram: form.medicaidProgram,
    payerName: form.payerName,
    serviceCategory: form.serviceCategory,
    requestType: form.requestType,
    unitType: form.unitType,
    minutesPerUnit: form.minutesPerUnit,
    requestedStartDate: form.requestedStartDate,
    requestedEndDate: form.requestedEndDate,
    revisionReason: form.revisionReason || null,
    linkedFormId: form.linkedFormId || null,
    linkedFormType: (form.linkedFormType as PALinkedFormType) || null,
    linkedFlowSheetFormId: form.linkedFlowSheetFormId || null,
    therapyAcuity: (form.therapyAcuity as TherapyAcuity) || null,
    attendantProgram: (form.attendantProgram as AttendantProgram) || null
  });

  /** Create (once) or update the PA; in create mode staged files upload right after. */
  const persistDraft = async (): Promise<Authorization | null> => {
    if (isNew) {
      if (createdRef.current) {
        return AuthorizationsService.updateAuthorization(
          patientId, authIdOf(createdRef.current), buildUpdatePayload(createdRef.current)
        );
      }
      const created = await AuthorizationsService.createPA(patientId, buildCreatePayload());
      createdRef.current = created;
      if (pendingFiles.length > 0) {
        const files = pendingFiles;
        setPendingFiles([]);
        const failures = await uploadFilesTo(authIdOf(created), files);
        if (failures.length > 0) onError(`PA saved, but some documents did not upload: ${failures.join(' ')}`);
      }
      return created;
    }
    if (authorization) {
      return AuthorizationsService.updateAuthorization(patientId, authorization.id, buildUpdatePayload(authorization));
    }
    return null;
  };

  const handleSaveAsDraft = useCallback(async () => {
    // Validate required fields (§9.1)
    const missingFields = missingRequiredFields();
    if (missingFields.length > 0) {
      onError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (form.requestType === PARequestType.REVISION && (!form.revisionReason || form.revisionReason.trim().length < 10)) {
      onError('Reason for Revision must be at least 10 characters');
      return;
    }

    const today = new Date();
    const startDate = new Date(form.requestedStartDate);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    if (startDate < thirtyDaysAgo) {
      onError('Start date cannot be more than 30 days in the past');
      return;
    }

    const endDate = new Date(form.requestedEndDate);
    if (endDate <= startDate) {
      onError('End date must be after start date');
      return;
    }

    const maxEndDate = new Date(startDate);
    maxEndDate.setDate(startDate.getDate() + 120);
    if (endDate > maxEndDate) {
      onError('End date cannot be more than 120 days after start date');
      return;
    }

    try {
      setSaving(true);
      const saved = await persistDraft();
      if (saved) onSaved(saved);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to save authorization'));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isNew, authorization, patientId, pendingFiles, onSaved, onError, uploadFilesTo]);

  // ── Submit: preflight (warnings) → confirm → submit; FLOWSHEET_INCOMPLETE blocks ──

  /** FLOWSHEET_INCOMPLETE → the gaps banner; anything else → the page error. */
  const showSubmitError = useCallback((err: unknown, fallback: string) => {
    const gaps = AuthorizationsService.getFlowSheetErrors(err);
    if (gaps) {
      setFlowSheetErrors(gaps);
      return;
    }
    onError(paErrorMessage(err, fallback));
  }, [onError]);

  const finishSubmit = useCallback(async (authId: string, acknowledgeWarnings: boolean, fallback: Authorization | null) => {
    try {
      const submitted = await AuthorizationsService.submitPA(patientId, authId, { acknowledgeWarnings });
      setFlowSheetErrors(null);
      onSaved(submitted);
    } catch (err) {
      showSubmitError(err, 'Failed to submit authorization');
      if (fallback) onSaved(fallback);
    }
  }, [patientId, onSaved, showSubmitError]);

  /** Preflight; opens the warning dialog or submits straight away. */
  const startSubmit = useCallback(async (authId: string, fallback: Authorization | null) => {
    setFlowSheetErrors(null);
    let warnings: PASubmitWarning[] = [];
    try {
      warnings = (await AuthorizationsService.preflightSubmit(patientId, authId)).warnings;
    } catch (err) {
      showSubmitError(err, 'Failed to submit authorization');
      if (fallback) onSaved(fallback);
      return;
    }
    if (warnings.length > 0) {
      setPendingSubmit({ authId, warnings, fallback });
      return;
    }
    await finishSubmit(authId, false, fallback);
  }, [patientId, onSaved, showSubmitError, finishSubmit]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!pendingSubmit) return;
    const { authId, fallback } = pendingSubmit;
    setSaving(true);
    setSavingTransition(true);
    try {
      await finishSubmit(authId, true, fallback);
    } finally {
      setPendingSubmit(null);
      setSaving(false);
      setSavingTransition(false);
    }
  }, [pendingSubmit, finishSubmit]);

  const handleCancelSubmit = useCallback(() => {
    const fallback = pendingSubmit?.fallback || null;
    setPendingSubmit(null);
    // The edits were saved before the preflight; hand back the saved draft
    if (fallback) onSaved(fallback);
  }, [pendingSubmit, onSaved]);

  const handleMarkAsSubmitted = useCallback(async () => {
    // Validate all required fields (§9.2 — same as draft plus all required must be populated)
    const missingFields = missingRequiredFields();
    if (form.requestType === PARequestType.REVISION && (!form.revisionReason || form.revisionReason.trim().length < 10)) {
      onError('Reason for Revision must be at least 10 characters');
      return;
    }
    if (missingFields.length > 0) {
      onError(`Please fill in all required fields before submitting: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      const saved = await persistDraft();
      if (!saved) return;
      await startSubmit(authIdOf(saved), saved);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to submit authorization'));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isNew, authorization, patientId, pendingFiles, onError, startSubmit, uploadFilesTo]);

  // ── Decision / transition handlers (view mode only) ───────────────────

  const handleApprovalFormChange = useCallback((field: string, value: string | number | boolean) => {
    setApprovalForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleConfirmApproval = useCallback(async () => {
    if (!authorization) return;
    const missing: string[] = [];
    if (!approvalForm.authNumber?.toString().trim()) missing.push('Auth Number');
    if (!approvalForm.approvedStartDate) missing.push('Approved Start');
    if (!approvalForm.approvedEndDate) missing.push('Approved End');
    if (!approvalForm.approvedQuantity) missing.push('Total Approved');
    if (!approvalForm.approvedPerWeek) missing.push('Per Week');
    if (missing.length > 0) {
      onError(`Please fill in: ${missing.join(', ')}`);
      return;
    }
    try {
      setSavingTransition(true);
      const approved = await AuthorizationsService.approvePA(
        patientId,
        authIdOf(authorization),
        {
          authNumber: String(approvalForm.authNumber),
          approvedStartDate: approvalForm.approvedStartDate,
          approvedEndDate: approvalForm.approvedEndDate,
          approvedQuantity: typeof approvalForm.approvedQuantity === 'string' ? parseInt(approvalForm.approvedQuantity) || 0 : approvalForm.approvedQuantity,
          approvedPerWeek: typeof approvalForm.approvedPerWeek === 'string' ? parseInt(approvalForm.approvedPerWeek) || 0 : approvalForm.approvedPerWeek,
          serviceType: (approvalForm.serviceType as PAServiceType) || null,
          uaApproved: approvalForm.uaApproved === true,
        }
      );
      setShowApprovalForm(false);
      // PA-03: e.g. approved after its end date → saved as EXPIRED
      if (approved.notice) showToast(approved.notice, { variant: 'warning', durationMs: 10000 });
      onSaved(approved);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to approve authorization'));
    } finally {
      setSavingTransition(false);
    }
  }, [authorization, approvalForm, patientId, onSaved, onError, showToast]);

  const handleDeny = useCallback(async () => {
    if (!authorization) return;
    setConfirmDenyOpen(false);
    try {
      setSavingTransition(true);
      const denied = await AuthorizationsService.denyPA(patientId, authIdOf(authorization));
      onSaved(denied);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to deny authorization'));
    } finally {
      setSavingTransition(false);
    }
  }, [authorization, patientId, onSaved, onError]);

  const handlePend = useCallback(async () => {
    if (!authorization) return;
    try {
      setSavingTransition(true);
      const pended = await AuthorizationsService.pendPA(patientId, authIdOf(authorization));
      onSaved(pended);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to pend authorization'));
    } finally {
      setSavingTransition(false);
    }
  }, [authorization, patientId, onSaved, onError]);

  const handleResubmit = useCallback(async () => {
    if (!authorization) return;
    try {
      setSavingTransition(true);
      await startSubmit(authIdOf(authorization), null);
    } finally {
      setSavingTransition(false);
    }
  }, [authorization, startSubmit]);

  const handleCopy = useCallback(async () => {
    if (!authorization) return;
    try {
      setSavingTransition(true);
      const copied = await AuthorizationsService.copyPA(patientId, authIdOf(authorization));
      onSaved(copied);
    } catch (err) {
      onError(paErrorMessage(err, 'Failed to copy authorization'));
    } finally {
      setSavingTransition(false);
    }
  }, [authorization, patientId, onSaved, onError]);

  const handleGeneratePacket = useCallback(async () => {
    if (!authorization) return;
    setPacketError(null);
    setPacketResult(null);
    setPacketLoading(true);
    try {
      const result = await AuthorizationsService.generatePacket(patientId, authIdOf(authorization));
      setPacketResult(result);
      window.open(result.url, '_blank', 'noopener');
    } catch (err) {
      const agencyMissing = getAgencyProfileMissing(err);
      const missing = agencyMissing ?? AuthorizationsService.getMissingFields(err);
      const message = agencyMissing
        ? 'The packet can’t be generated until the agency profile is complete.'
        : paErrorMessage(err, 'Failed to generate the PA packet.');
      setPacketError({ message, missing });
    } finally {
      setPacketLoading(false);
    }
  }, [authorization, patientId]);

  // ── Shared pieces ─────────────────────────────────────────────────────

  const feedbackBanners = (
    <>
      {status === PAStatus.DRAFT && <DeadlinesBanner deadlines={authorization?.deadlines} />}
      {flowSheetErrors && <FlowSheetErrorsBanner errors={flowSheetErrors} onDismiss={() => setFlowSheetErrors(null)} />}
      <PacketBanner
        result={packetResult}
        error={packetError}
        onDismiss={() => { setPacketResult(null); setPacketError(null); }}
      />
    </>
  );

  const uploadProgressBar = uploadProgress && (
    <div className="px-1" role="status" aria-live="polite" data-testid="pa-upload-progress">
      <div className="flex justify-between gap-2 text-xs text-slate-600">
        <span className="min-w-0 truncate">Uploading {uploadProgress.name}</span>
        <span className="shrink-0">{uploadProgress.pct}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded bg-slate-200">
        <div className="h-1.5 rounded bg-blue-500 transition-all" style={{ width: `${uploadProgress.pct}%` }} />
      </div>
    </div>
  );

  const dialogs = (
    <>
      <ConfirmDialog
        open={pendingSubmit !== null}
        title="Submit with warnings?"
        message={<SubmitWarningsList warnings={pendingSubmit?.warnings || []} />}
        confirmLabel="Submit anyway"
        busy={savingTransition}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete document"
        message={`Delete "${deleteTarget?.name || ''}" from this PA? It is removed from the list; the audit history is kept.`}
        confirmLabel="Delete"
        destructive
        busy={viewDeletingId !== null}
        onConfirm={confirmDeleteDocument}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );

  // ── Render: Create / Edit Form ────────────────────────────────────────

  if (isNew || (isEdit && canEdit)) {
    const busy = saving || uploading;
    const createEditActions: ActionButtonConfig[] = isNew
      ? [
          {
            key: 'cancel',
            label: 'Cancel',
            onClick: onBack,
            variant: 'secondary',
            disabled: busy,
          },
          {
            key: 'add',
            label: 'Add',
            loadingLabel: 'Adding...',
            onClick: handleSaveAsDraft,
            variant: 'primary',
            disabled: busy,
            loading: saving,
          },
        ]
      : [
          {
            key: 'cancel',
            label: 'Cancel',
            onClick: onBack,
            variant: 'secondary',
            disabled: busy,
          },
          {
            key: 'packet',
            label: 'Generate packet',
            loadingLabel: 'Generating...',
            onClick: handleGeneratePacket,
            variant: 'secondary',
            disabled: busy || packetLoading,
            loading: packetLoading,
            hidden: !canGeneratePacket || !isPARecord,
          },
          {
            key: 'save-draft',
            label: 'Save as Draft',
            loadingLabel: 'Saving...',
            onClick: handleSaveAsDraft,
            variant: 'secondary',
            disabled: busy,
            loading: saving,
          },
          {
            key: 'submit',
            label: 'Submit',
            loadingLabel: 'Submitting...',
            onClick: handleMarkAsSubmitted,
            variant: 'primary',
            disabled: busy,
            loading: saving,
          },
        ];

    return (
      <FormPageLayout
        onBack={onBack}
        actions={createEditActions}
        title={isNew ? 'New PA Request' : 'Edit PA Request'}
        subtitle={[patientName, medicaidId ? `Medicaid: ${medicaidId}` : null].filter(Boolean).join(' · ') || undefined}
        headerRight={<StatusBadge status={isNew ? PAStatus.DRAFT : status} />}
      >
        <GuideBanner status={status} />
        {feedbackBanners}

        <PayerSection
          patientId={patientId}
          payerType={form.payerType}
          medicaidProgram={form.medicaidProgram}
          payerName={form.payerName}
          authNumber={form.authNumber}
          authNumberLocked={!!authorization}
          onChange={handleFormChange}
        />

        <RequestSection
          serviceCategory={form.serviceCategory}
          requestType={form.requestType}
          unitType={form.unitType}
          minutesPerUnit={form.minutesPerUnit}
          requestedStartDate={form.requestedStartDate}
          requestedEndDate={form.requestedEndDate}
          revisionReason={form.revisionReason}
          therapyAcuity={form.therapyAcuity}
          attendantProgram={form.attendantProgram}
          onChange={handleFormChange}
        />

        <FormAssociationSection
          patientId={patientId}
          linkedFormId={form.linkedFormId}
          linkedFormType={form.linkedFormType}
          serviceCategory={form.serviceCategory}
          onChange={handleFormChange}
        />

        <LinkedFlowSheetSection
          patientId={patientId}
          linkedFlowSheetFormId={form.linkedFlowSheetFormId}
          onChange={handleFormChange}
          required={needsFlowSheet(form.payerType, form.serviceCategory)}
          unitType={form.unitType}
          minutesPerUnit={form.minutesPerUnit}
          onError={onError}
        />

        <DocumentUpload
          collapsible
          defaultOpen
          canUpload={!uploading}
          documents={isNew ? [] : viewDocs}
          stagedFiles={isNew ? pendingFiles : []}
          uploading={uploading}
          onFilesSelected={isNew ? handleFileSelect : handleViewUpload}
          onStagedRemove={(i) => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
          onDownload={isNew ? undefined : handleViewDownload}
          onDelete={isNew || !canDeleteDocs ? undefined : (doc) => setDeleteTarget(doc)}
          deletingId={isNew ? null : viewDeletingId}
          onValidationError={onError}
        />
        {isNew && pendingFiles.length > 0 && (
          <p className="px-1 text-xs text-slate-500 text-left">Files upload when the PA is first saved.</p>
        )}
        {uploadProgressBar}
        {dialogs}
      </FormPageLayout>
    );
  }

  // ── Render: View Detail ────────────────────────────────────────────────

  if (!authorization) return null;

  const ut = (authorization.unitType || PAUnitType.UNITS) as PAUnitType;
  const mpu = authorization.minutesPerUnit || null;
  const svc = (authorization.serviceCategory || '') as ServiceCategory;

  const viewActions: ActionButtonConfig[] = [
    {
      key: 'deny',
      label: 'Denied',
      onClick: () => setConfirmDenyOpen(true),
      variant: 'danger',
      disabled: savingTransition,
      loading: savingTransition,
      loadingLabel: 'Denying...',
      hidden: isReadOnlyView || status !== PAStatus.SUBMITTED,
    },
    {
      key: 'pend',
      label: 'Pended',
      onClick: handlePend,
      variant: 'warning',
      disabled: savingTransition,
      loading: savingTransition,
      loadingLabel: 'Pending...',
      hidden: isReadOnlyView || status !== PAStatus.SUBMITTED,
    },
    {
      key: 'approve',
      label: 'Approved',
      onClick: () => setShowApprovalForm(true),
      variant: 'success',
      disabled: savingTransition,
      hidden: isReadOnlyView || status !== PAStatus.SUBMITTED,
    },
    {
      key: 'packet',
      label: 'Generate packet',
      onClick: handleGeneratePacket,
      variant: 'secondary',
      disabled: packetLoading,
      loading: packetLoading,
      loadingLabel: 'Generating...',
      hidden: !canGeneratePacket || !isPARecord,
      className: 'text-slate-600 border border-slate-300 hover:bg-slate-50',
    },
    {
      key: 'resubmit',
      label: 'Resubmit',
      onClick: handleResubmit,
      variant: 'primary',
      disabled: savingTransition,
      loading: savingTransition,
      loadingLabel: 'Submitting...',
      hidden: isReadOnlyView || status !== PAStatus.PENDED,
    },
    {
      key: 'copy',
      label: 'Copy as New',
      onClick: handleCopy,
      variant: 'secondary',
      disabled: savingTransition,
      loading: savingTransition,
      loadingLabel: 'Copying...',
      hidden: isReadOnlyView || !canUpdateAuthorization || !PA_COPYABLE_STATUSES.has(status),
      className: 'text-slate-600 border border-slate-300 hover:bg-slate-50',
    },
  ];

  const viewTitle = authorization.paNumber || String(authorization.authorizationId || authorization.id);

  const viewSubtitle = [
    patientName,
    medicaidId ? `Medicaid: ${medicaidId}` : null,
    status === PAStatus.SUBMITTED && authorization.updatedAt
      ? `Submitted: ${AuthorizationsService.formatDate(String(authorization.updatedAt))}`
      : null,
    authorization.authNumber ? `Auth: ${authorization.authNumber}` : null,
    AuthorizationsService.getDurationLabel(mpu),
  ].filter(Boolean).join(' · ');

  const viewHeaderRight = (
    <>
      {svc && <SvcBadge serviceCategory={svc} />}
      <StatusBadge status={status} />
      {authorization.payerType && authorization.payerName && (
        <PayerChip payerType={authorization.payerType} payerName={authorization.payerName} />
      )}
    </>
  );

  return (
    <FormPageLayout
      onBack={onBack}
      actions={viewActions}
      title={viewTitle}
      subtitle={viewSubtitle || undefined}
      headerRight={viewHeaderRight}
    >
      <GuideBanner status={status} expirationAlertLevel={authorization.expirationAlertLevel} expirationDaysRemaining={authorization.expirationDaysRemaining} />
      {feedbackBanners}

      {/* Read-only mode notification */}
      {isReadOnlyView && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-700">
            You do not have permission to edit Prior Authorizations. Viewing in read-only mode.
          </p>
        </div>
      )}

      {/* Approval Entry Form — shown when user clicks "Approved" from SUBMITTED state */}
      {showApprovalForm && (
        <ApprovalSection
          formMode
          formData={approvalForm}
          onFormChange={handleApprovalFormChange}
          onConfirm={handleConfirmApproval}
          onCancel={() => setShowApprovalForm(false)}
          saving={savingTransition}
          unitType={ut}
          minutesPerUnit={mpu}
          serviceCategory={svc}
          requestedStartDate={authorization.requestedStartDate}
          requestedEndDate={authorization.requestedEndDate}
        />
      )}

      {/* Usage Tracker (approved-like only) */}
      {isApprovedLike && (
        <UsageTrackerSection
          patientId={patientId}
          authId={authIdOf(authorization)}
          status={status}
          usedQuantity={authorization.usedQuantity || 0}
          approvedQuantity={authorization.approvedQuantity || 0}
          approvedPerWeek={authorization.approvedPerWeek || null}
          daysLeft={daysLeft}
          unitType={ut}
          minutesPerUnit={mpu}
          isLive={status === PAStatus.APPROVED}
          defaultOpen
          canManageLinks={canUpdateAuthorization && !isReadOnlyView}
          approvedStartDate={authorization.approvedStartDate}
          approvedEndDate={authorization.approvedEndDate}
        />
      )}

      {/* Approval Details (approved-like only) */}
      {isApprovedLike && (
        <ApprovalSection
          readOnly
          unitType={ut}
          minutesPerUnit={mpu}
          serviceCategory={svc}
          authNumber={authorization.authNumber}
          approvedStartDate={authorization.approvedStartDate}
          approvedEndDate={authorization.approvedEndDate}
          approvedQuantity={authorization.approvedQuantity}
          approvedPerWeek={authorization.approvedPerWeek}
          serviceType={authorization.serviceType}
          uaApproved={authorization.uaApproved === true}
        />
      )}

      {/* Request Details (read-only) */}
      <RequestSection
        serviceCategory={authorization.serviceCategory || ''}
        requestType={authorization.requestType || ''}
        unitType={ut}
        minutesPerUnit={mpu}
        requestedStartDate={authorization.requestedStartDate || ''}
        requestedEndDate={authorization.requestedEndDate || ''}
        revisionReason={authorization.revisionReason || ''}
        therapyAcuity={authorization.therapyAcuity || ''}
        attendantProgram={authorization.attendantProgram || ''}
        onChange={() => {}}
        readOnly
        defaultOpen={status === PAStatus.SUBMITTED || status === PAStatus.PENDED}
      />

      <FormAssociationSection
        patientId={patientId}
        linkedFormId={authorization.linkedFormId || ''}
        linkedFormType={authorization.linkedFormType || ''}
        serviceCategory={svc}
        onChange={() => {}}
        readOnly
        defaultOpen={false}
      />

      {(authorization.linkedFlowSheetFormId || needsFlowSheet(authorization.payerType || '', svc)) && (
        <LinkedFlowSheetSection
          patientId={patientId}
          linkedFlowSheetFormId={authorization.linkedFlowSheetFormId || ''}
          onChange={() => {}}
          readOnly
          required={needsFlowSheet(authorization.payerType || '', svc) && (status === PAStatus.DRAFT || status === PAStatus.PENDED)}
          approvedPerWeek={authorization.approvedPerWeek}
          unitType={ut}
          minutesPerUnit={mpu}
          onError={onError}
          defaultOpen={status === PAStatus.DRAFT || status === PAStatus.PENDED}
        />
      )}

      {/* Payer (read-only) */}
      <PayerSection
        payerType={authorization.payerType || ''}
        medicaidProgram={authorization.medicaidProgram || ''}
        payerName={authorization.payerName || ''}
        onChange={() => {}}
        readOnly
        defaultOpen={false}
      />

      {/* Documents */}
      <DocumentUpload
        collapsible
        defaultOpen={status === PAStatus.PENDED}
        canUpload={!isReadOnlyView && canUpdateAuthorization && !uploading && (status === PAStatus.SUBMITTED || status === PAStatus.PENDED || status === PAStatus.DRAFT)}
        documents={viewDocs}
        uploading={uploading}
        onFilesSelected={handleViewUpload}
        onDownload={handleViewDownload}
        onDelete={canDeleteDocs ? (doc) => setDeleteTarget(doc) : undefined}
        deletingId={viewDeletingId}
        onValidationError={onError}
      />
      {uploadProgressBar}
      <ConfirmDialog
        open={confirmDenyOpen}
        title="Mark as denied"
        message="Mark this authorization as Denied? This cannot be undone."
        confirmLabel="Mark denied"
        destructive
        busy={savingTransition}
        onConfirm={handleDeny}
        onCancel={() => setConfirmDenyOpen(false)}
      />
      {dialogs}
    </FormPageLayout>
  );
};

export default AuthorizationDetail;
